import { useCallback, useMemo, useState } from 'react';
import shuffle from 'lodash/shuffle';

import { useMutation, useQuery } from '@apollo/client';

import type {
  AnswersType,
  DuelRoundQuestionAnswerType,
  MutationSetOnboardingAnswerArgs,
  QuestionType,
} from '@/graphql';
import { ONBOARDING_QUESTIONS } from '@/apollo/queries';
import { SET_ONBOARDING_ANSWER } from '@/apollo/mutation';
import { datadogRum } from '@datadog/browser-rum';

type OnboardingQuestion = Pick<
  QuestionType,
  'id' | 'value' | 'questionImage' | 'explanation' | 'sortedLinks' | 'answers'
>;

type OnboardingAnswer = Pick<AnswersType, 'id' | 'value'>;

type OnboardingAnswerState = {
  answerIsCorrect?: boolean | null;
  userAnswer?: OnboardingAnswer | null;
  correctAnswer?: OnboardingAnswer | null;
};

type UserOnboardingQuestionAnswerData = {
  answerIsCorrect?: boolean | null;
  question?: Pick<OnboardingQuestion, 'id'> | null;
  userAnswer?: OnboardingAnswer | null;
  correctAnswer?: OnboardingAnswer | null;
};

type OnboardingQuestionsQueryData = {
  onboardingQuestions?: Array<OnboardingQuestion | null> | null;
};

type SetOnboardingAnswerMutationData = {
  setOnboardingAnswer?: {
    userOnboardingQuestionAnswerData?: UserOnboardingQuestionAnswerData | null;
  } | null;
};

type DerivedState = {
  index: number;
  currentQuestion: OnboardingQuestion | null;
  currentQuestionAnswer: OnboardingAnswerState | null;
  hasAnswer: boolean;
  isCorrect: boolean;
  isInCorrect: boolean;
  answeredQuestionForView: DuelRoundQuestionAnswerType | null;
  highlightedDotIndex: number;
  nextLink: string;
};

type UseOnboardingQuestionStateArgs = {
  questionId?: string;
};

const isNotNullQuestion = (
  question: OnboardingQuestion | null | undefined
): question is OnboardingQuestion => !!question;

const toAnswerState = (
  answerData?: UserOnboardingQuestionAnswerData | null
): OnboardingAnswerState => ({
  answerIsCorrect: answerData?.answerIsCorrect ?? null,
  userAnswer: answerData?.userAnswer
    ? {
        id: answerData.userAnswer.id,
        value: answerData.userAnswer.value,
      }
    : null,
  correctAnswer: answerData?.correctAnswer
    ? {
        id: answerData.correctAnswer.id,
        value: answerData.correctAnswer.value,
      }
    : null,
});

const buildAnsweredQuestionForView = (
  question: OnboardingQuestion | null,
  answerState: OnboardingAnswerState | null
): DuelRoundQuestionAnswerType | null => {
  if (!question || !answerState) return null;

  const fallbackCorrectAnswer = question.answers.find((item) => item.isCorrect) ?? null;

  return {
    question: {
      ...question,
      answers: question.answers || [],
    },
    userAnswer: answerState.userAnswer ?? null,
    correctAnswer: answerState.correctAnswer ?? fallbackCorrectAnswer,
  } as DuelRoundQuestionAnswerType;
};

const getOptimisticAnswerData = (
  question: OnboardingQuestion,
  selectedAnswerId: string
): UserOnboardingQuestionAnswerData => {
  const selectedAnswer = question.answers.find((answer) => String(answer.id) === selectedAnswerId);
  const correctAnswer = question.answers.find((answer) => answer.isCorrect);

  return {
    answerIsCorrect:
      !!selectedAnswer && !!correctAnswer ? selectedAnswer.id === correctAnswer.id : null,
    question: {
      id: question.id,
    },
    userAnswer: selectedAnswer
      ? {
          id: selectedAnswer.id,
          value: selectedAnswer.value,
        }
      : null,
    correctAnswer: correctAnswer
      ? {
          id: correctAnswer.id,
          value: correctAnswer.value,
        }
      : null,
  };
};

export const useOnboardingQuestionState = ({ questionId }: UseOnboardingQuestionStateArgs) => {
  const [answerId, setAnswerId] = useState<string | null>(null);
  const [answersByQuestionId, setAnswersByQuestionId] = useState<
    Record<string, OnboardingAnswerState>
  >({});

  const { data, error, loading } = useQuery<OnboardingQuestionsQueryData>(ONBOARDING_QUESTIONS);
  const [setOnboardingAnswer, { loading: setAnswerLoading }] = useMutation<
    SetOnboardingAnswerMutationData,
    MutationSetOnboardingAnswerArgs
  >(SET_ONBOARDING_ANSWER);

  const userQuestions = useMemo(
    () => (data?.onboardingQuestions || []).filter(isNotNullQuestion),
    [data?.onboardingQuestions]
  );

  const derivedState = useMemo<DerivedState>(() => {
    const index = userQuestions.findIndex((question) => String(question.id) === questionId);
    const currentQuestion =
      index >= 0 && index < userQuestions.length ? userQuestions[index] : null;
    const currentQuestionAnswer = currentQuestion
      ? answersByQuestionId[String(currentQuestion.id)] || null
      : null;
    const hasAnswer = !!currentQuestionAnswer;
    const isCorrect = currentQuestionAnswer?.answerIsCorrect === true;
    const isInCorrect = currentQuestionAnswer?.answerIsCorrect === false;
    const highlightedDotIndex =
      index < 0 ? -1 : hasAnswer ? Math.min(index + 1, userQuestions.length - 1) : index;

    let nextLink = '';
    if (index >= 0) {
      if (index >= userQuestions.length - 1) {
        nextLink = '/onboard-final';
      } else {
        const nextQuestion = userQuestions[index + 1];
        nextLink = `/onboard-questions/${nextQuestion.id}/completed`;
      }
    }

    return {
      index,
      currentQuestion,
      currentQuestionAnswer,
      hasAnswer,
      isCorrect,
      isInCorrect,
      answeredQuestionForView: buildAnsweredQuestionForView(currentQuestion, currentQuestionAnswer),
      highlightedDotIndex,
      nextLink,
    };
  }, [answersByQuestionId, questionId, userQuestions]);

  const options = useMemo(() => {
    if (!derivedState.currentQuestion?.answers) {
      return [...Array(4)].map(() => ({
        value: null,
        label: null,
      }));
    }

    const answersMixed = shuffle(derivedState.currentQuestion.answers || []);
    return answersMixed.map((answer) => ({
      value: String(answer.id),
      label: answer.value,
    }));
  }, [derivedState.currentQuestion]);

  const chooseAnswer = useCallback(
    (selectedAnswerId: string) => {
      setAnswerId(selectedAnswerId);
      datadogRum.addAction('DT choose answer', {
        questionId,
        answerId: selectedAnswerId,
      });
    },
    [questionId]
  );

  const submitAnswer = useCallback(async () => {
    try {
      if (!derivedState.currentQuestion?.id || !answerId) return;

      const optimisticAnswerData = getOptimisticAnswerData(derivedState.currentQuestion, answerId);

      const response = await setOnboardingAnswer({
        variables: {
          questionId: String(derivedState.currentQuestion.id),
          answerId,
        },
        optimisticResponse: {
          setOnboardingAnswer: {
            userOnboardingQuestionAnswerData: optimisticAnswerData,
          },
        },
      });

      const answerData =
        response?.data?.setOnboardingAnswer?.userOnboardingQuestionAnswerData || optimisticAnswerData;
      const answeredQuestionId = String(answerData.question?.id || derivedState.currentQuestion.id);

      setAnswersByQuestionId((prev) => ({
        ...prev,
        [answeredQuestionId]: toAnswerState(answerData),
      }));

      datadogRum.addAction('DT set answer', {
        questionId,
        answerId,
      });
    } catch (mutationError) {
      console.error('SET_ANSWER', mutationError);
    } finally {
      setAnswerId(null);
    }
  }, [answerId, derivedState.currentQuestion, questionId, setOnboardingAnswer]);

  const getDotVariant = useCallback(
    (question: OnboardingQuestion, index: number) => {
      const answerState = answersByQuestionId[String(question.id)];
      if (!answerState) return index === derivedState.highlightedDotIndex ? 'highlighted' : 'default';

      return answerState.answerIsCorrect ? 'success' : 'error';
    },
    [answersByQuestionId, derivedState.highlightedDotIndex]
  );

  return {
    answerId,
    chooseAnswer,
    currentQuestion: derivedState.currentQuestion,
    error,
    getDotVariant,
    hasAnswer: derivedState.hasAnswer,
    index: derivedState.index,
    isCorrect: derivedState.isCorrect,
    isInCorrect: derivedState.isInCorrect,
    loading,
    nextLink: derivedState.nextLink,
    options,
    questionAnswerForView: derivedState.answeredQuestionForView,
    setAnswerLoading,
    submitAnswer,
    userQuestions,
  };
};
